
import './StaffAssignment.css';
// components/StaffAssignment.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sectionService } from '../services/sectionService';

const StaffAssignment = ({ user, addNotification }) => {
  const [sections, setSections] = useState([]);
  const [myAssignments, setMyAssignments] = useState({});
  const [userInfo, setUserInfo] = useState({ first_name: '', last_name: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [position, setPosition] = useState('coach');
  const [isPrimary, setIsPrimary] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('nlngra_user');
      let memberId = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          memberId = userData.id;
        } catch(e) {}
      }
      
      const data = await sectionService.getAvailableSectionsForStaff(memberId);
      
      if (data && data.success === true) {
        setSections(data.sections || []);
        setMyAssignments(data.my_assignments || {});
        
        if (data.user_info && typeof data.user_info === 'object') {
          setUserInfo({
            first_name: data.user_info.first_name || '',
            last_name: data.user_info.last_name || ''
          });
        }
        
        if (addNotification) {
          addNotification(`Loaded ${data.sections.length} sections`, 'success');
        }
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      if (addNotification) addNotification('Failed to load sections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    const cats = ['all', ...new Set(sections.map(s => s?.category).filter(Boolean))];
    return cats;
  };

  const filteredSections = sections.filter(section => {
    const matchesCategory = selectedCategory === 'all' || section?.category === selectedCategory;
    const matchesSearch = section?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          section?.category?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleOpenModal = (section) => {
    const existing = myAssignments[section.id];
    setSelectedSection(section);
    setPosition(existing?.position || 'coach');
    setIsPrimary(existing?.is_primary || false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSection(null);
    setPosition('coach');
    setIsPrimary(false);
  };

  const handleSaveAssignment = async () => {
    if (!selectedSection) return;

    setSaving(true);
    try {
      const userStr = localStorage.getItem('nlngra_user');
      let memberId = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          memberId = userData.id;
        } catch(e) {}
      }
      
      const updatedAssignments = { ...myAssignments };
      updatedAssignments[selectedSection.id] = {
        position: position,
        is_primary: isPrimary
      };
      
      const assignmentsArray = Object.entries(updatedAssignments).map(([id, data]) => ({
        section_id: parseInt(id),
        position: data.position,
        is_primary: data.is_primary ? 1 : 0
      }));
      
      const data = await sectionService.updateStaffPositions(assignmentsArray, memberId);
      
      if (data.success) {
        if (addNotification) addNotification('Role assigned successfully!', 'success');
        fetchSections();
        handleCloseModal();
      } else {
        if (addNotification) addNotification(data.message || 'Failed to assign role', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      if (addNotification) addNotification('Failed to assign role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAssignment = async (sectionId) => {
    if (!window.confirm('Remove your role from this section?')) return;
    
    setSaving(true);
    try {
      const userStr = localStorage.getItem('nlngra_user');
      let memberId = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          memberId = userData.id;
        } catch(e) {}
      }
      
      const updatedAssignments = { ...myAssignments };
      delete updatedAssignments[sectionId];
      
      const assignmentsArray = Object.entries(updatedAssignments).map(([id, data]) => ({
        section_id: parseInt(id),
        position: data.position,
        is_primary: data.is_primary ? 1 : 0
      }));
      
      const data = await sectionService.updateStaffPositions(assignmentsArray, memberId);
      
      if (data.success) {
        if (addNotification) addNotification('Role removed successfully!', 'success');
        fetchSections();
      } else {
        if (addNotification) addNotification(data.message || 'Failed to remove role', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      if (addNotification) addNotification('Failed to remove role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (sectionId) => {
    const role = myAssignments[sectionId];
    if (!role) return null;
    
    const labels = {
      coach: { text: 'Coach', icon: 'fa-chalkboard-user', color: '#27a85a', bg: '#e8f5e9' },
      captain: { text: 'Captain', icon: 'fa-crown', color: '#d97706', bg: '#fef3c7' },
      assistant_coach: { text: 'Asst. Coach', icon: 'fa-user-friends', color: '#3b82f6', bg: '#eff6ff' },
      vice_captain: { text: 'Vice Captain', icon: 'fa-shield-alt', color: '#8b5cf6', bg: '#f3e8ff' }
    };
    
    const info = labels[role.position] || labels.coach;
    return (
      <span className="role-tag" style={{ background: info.bg, color: info.color }}>
        <i className={`fas ${info.icon}`}></i>
        {info.text}
        {role.is_primary && <span className="primary-dot">★</span>}
      </span>
    );
  };

  const stats = {
    total: sections.length,
    assigned: Object.keys(myAssignments).length,
    coaches: Object.values(myAssignments).filter(r => r.position === 'coach').length,
    captains: Object.values(myAssignments).filter(r => r.position === 'captain').length
  };

  if (loading) {
    return (
      <div className="sa-loading">
        <div className="sa-spinner"></div>
        <p>Loading sections...</p>
      </div>
    );
  }

  return (
    <div className="sa-container">
      {/* Header */}
      <div className="sa-header">
        <div className="sa-header-left">
          <div className="sa-logo">
            <i className="fas fa-users-between"></i>
          </div>
          <div>
            <h1>Staff Roles</h1>
            <p>Manage your coaching and captain positions</p>
          </div>
        </div>
        <div className="sa-user">
          <div className="sa-avatar">
            {userInfo.first_name?.charAt(0) || 'U'}
          </div>
          <div className="sa-user-info">
            <strong>{userInfo.first_name} {userInfo.last_name}</strong>
            <span>Primary roles update instructor info</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="sa-stats">
        <div className="sa-stat">
          <i className="fas fa-building"></i>
          <div>
            <span className="sa-stat-num">{stats.total}</span>
            <span className="sa-stat-label">Sections</span>
          </div>
        </div>
        <div className="sa-stat">
          <i className="fas fa-user-tag"></i>
          <div>
            <span className="sa-stat-num">{stats.assigned}</span>
            <span className="sa-stat-label">Assigned</span>
          </div>
        </div>
        <div className="sa-stat">
          <i className="fas fa-chalkboard-user"></i>
          <div>
            <span className="sa-stat-num">{stats.coaches}</span>
            <span className="sa-stat-label">Coaches</span>
          </div>
        </div>
        <div className="sa-stat">
          <i className="fas fa-crown"></i>
          <div>
            <span className="sa-stat-num">{stats.captains}</span>
            <span className="sa-stat-label">Captains</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sa-filters">
        <div className="sa-search">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Search sections..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="sa-categories">
          {getCategories().map(cat => (
            <button 
              key={cat}
              className={`sa-cat-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Sections Grid */}
      <div className="sa-grid">
        {filteredSections.map(section => (
          <div key={section.id} className="sa-card">
            <div className="sa-card-header">
              <div className="sa-card-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="sa-card-badge">{section.category}</div>
            </div>
            <div className="sa-card-body">
              <h3>{section.name}</h3>
              {section.instructor && (
                <p className="sa-instructor">
                  <i className="fas fa-chalkboard-user"></i>
                  {section.instructor}
                </p>
              )}
            </div>
            <div className="sa-card-footer">
              <div className="sa-role">
                {getRoleLabel(section.id) || <span className="no-role">No role assigned</span>}
              </div>
              <div className="sa-actions">
                <button className="sa-btn sa-btn-outline" onClick={() => handleOpenModal(section)}>
                  <i className="fas fa-edit"></i>
                  {myAssignments[section.id] ? 'Edit' : 'Assign'}
                </button>
                {myAssignments[section.id] && (
                  <button className="sa-btn sa-btn-danger" onClick={() => handleRemoveAssignment(section.id)}>
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredSections.length === 0 && (
        <div className="sa-empty">
          <i className="fas fa-folder-open"></i>
          <p>No sections found</p>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && selectedSection && (
          <motion.div 
            className="sa-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div 
              className="sa-modal"
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sa-modal-header">
                <div className="sa-modal-icon">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div>
                  <h3>Assign Role</h3>
                  <p>{selectedSection.name}</p>
                </div>
                <button className="sa-modal-close" onClick={handleCloseModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="sa-modal-body">
                <div className="sa-field">
                  <label>Select Position</label>
                  <div className="sa-positions">
                    <button 
                      className={`sa-pos ${position === 'coach' ? 'active' : ''}`}
                      onClick={() => setPosition('coach')}
                    >
                      <i className="fas fa-chalkboard-user"></i>
                      <span>Coach</span>
                    </button>
                    <button 
                      className={`sa-pos ${position === 'captain' ? 'active' : ''}`}
                      onClick={() => setPosition('captain')}
                    >
                      <i className="fas fa-crown"></i>
                      <span>Captain</span>
                    </button>
                    <button 
                      className={`sa-pos ${position === 'assistant_coach' ? 'active' : ''}`}
                      onClick={() => setPosition('assistant_coach')}
                    >
                      <i className="fas fa-user-friends"></i>
                      <span>Asst. Coach</span>
                    </button>
                    <button 
                      className={`sa-pos ${position === 'vice_captain' ? 'active' : ''}`}
                      onClick={() => setPosition('vice_captain')}
                    >
                      <i className="fas fa-shield-alt"></i>
                      <span>Vice Captain</span>
                    </button>
                  </div>
                </div>
                
                <div className="sa-field sa-primary">
                  <label className="sa-checkbox">
                    <input 
                      type="checkbox" 
                      checked={isPrimary}
                      onChange={(e) => setIsPrimary(e.target.checked)}
                    />
                    <span>
                      <strong>Primary Role</strong>
                      <small>Your name will appear as section instructor</small>
                    </span>
                  </label>
                </div>
                
                <div className="sa-note">
                  <i className="fas fa-envelope"></i>
                  <span>You'll receive email notifications for join requests</span>
                </div>
              </div>
              
              <div className="sa-modal-footer">
                <button className="sa-btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="sa-btn-save" onClick={handleSaveAssignment} disabled={saving}>
                  {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StaffAssignment;